import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileSpreadsheet, Trash2, Search, Loader2 } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';
import { useSession } from '../auth/SessionContext.jsx';

const PAGE_SIZE = 50;

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function resolveAnswer(question, value) {
  if (value === null || value === undefined || value === '') return null;
  if (question.type === 'flag') {
    return value ? 'Oui' : null;
  }
  if (question.type === 'select') {
    const option = question.options?.find((o) => o.value === value);
    return option?.label || String(value);
  }
  if (question.type === 'multiselect') {
    if (!Array.isArray(value)) return null;
    const labels = value
      .map((v) => {
        const option = question.options?.find((o) => o.value === v);
        return option?.label || String(v);
      })
      .filter(Boolean);
    return labels.length > 0 ? labels.join(', ') : null;
  }
  if (question.type === 'text' || question.type === 'textarea' || question.type === 'number') {
    const str = String(value).trim();
    return str.length > 0 ? str : null;
  }
  return null;
}

export default function ListView() {
  const { session } = useSession();
  const isAdmin = session?.role === 'admin';

  const [mode, setMode] = useState('avaloirs'); // 'avaloirs' | 'quick'

  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [page, setPage] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Quick-points (poubelles + cendriers)
  const [quickItems, setQuickItems] = useState([]);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickTypeFilter, setQuickTypeFilter] = useState('all'); // 'all' | 'poubelle' | 'cendrier' | 'zone_sensible'

  // Sélection multiple (admin only)
  const [selectedAvaloirIds, setSelectedAvaloirIds] = useState(new Set());
  const [selectedQuickIds, setSelectedQuickIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleAvaloirSelect = (id) => {
    setSelectedAvaloirIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleQuickSelect = (id) => {
    setSelectedQuickIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allAvaloirsSelected = items.length > 0 && items.every((a) => selectedAvaloirIds.has(a.id));
  const allQuickSelected = quickItems.length > 0 && quickItems.every((q) => selectedQuickIds.has(q.id));

  const toggleAllAvaloirs = () => {
    if (allAvaloirsSelected) setSelectedAvaloirIds(new Set());
    else setSelectedAvaloirIds(new Set(items.map((a) => a.id)));
  };
  const toggleAllQuick = () => {
    if (allQuickSelected) setSelectedQuickIds(new Set());
    else setSelectedQuickIds(new Set(quickItems.map((q) => q.id)));
  };

  const bulkDeleteAvaloirs = async () => {
    const ids = Array.from(selectedAvaloirIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer ${ids.length} avaloir${ids.length > 1 ? 's' : ''} ? Action irréversible.`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/api/avaloirs/bulk-delete', { ids });
      setSelectedAvaloirIds(new Set());
      // Refetch
      fetchItems();
      alert(`${res?.deleted ?? ids.length} avaloir${ids.length > 1 ? 's' : ''} supprimé${ids.length > 1 ? 's' : ''}.`);
    } catch (e) {
      alert('Suppression impossible : ' + (e.message || 'erreur'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const bulkDeleteQuick = async () => {
    const ids = Array.from(selectedQuickIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer ${ids.length} point${ids.length > 1 ? 's' : ''} ? Action irréversible.`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/api/quick-points/bulk-delete', { ids });
      setSelectedQuickIds(new Set());
      setQuickItems((prev) => prev.filter((q) => !ids.includes(q.id)));
      alert(`${res?.deleted ?? ids.length} point${ids.length > 1 ? 's' : ''} supprimé${ids.length > 1 ? 's' : ''}.`);
    } catch (e) {
      alert('Suppression impossible : ' + (e.message || 'erreur'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const fetchQuickItems = async () => {
    setQuickLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      if (quickTypeFilter !== 'all') params.set('type', quickTypeFilter);
      const data = await api.get(`/api/quick-points?${params.toString()}`);
      setQuickItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e.message || 'Erreur chargement quick-points');
      setQuickItems([]);
    } finally {
      setQuickLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'quick') fetchQuickItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, quickTypeFilter]);

  const onDeleteQuick = async (qp) => {
    const labelMap = {
      poubelle: 'cette poubelle',
      cendrier: 'ce cendrier',
      zone_sensible: 'cette zone sensible',
    };
    const label = labelMap[qp.type] || 'ce point';
    if (!window.confirm(`Supprimer ${label} ? Cette action est irréversible.`)) return;
    try {
      await api.del(`/api/quick-points/${qp.id}`);
      setQuickItems((prev) => prev.filter((x) => x.id !== qp.id));
    } catch (e) {
      console.error('[delete quick-point] failed', e);
      alert('Suppression impossible : ' + (e.message || 'erreur') + (e.status ? ` (HTTP ${e.status})` : ''));
    }
  };

  // Debounce search input
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(rawSearch.trim());
      setPage(0);
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [rawSearch]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (search) params.set('q', search);
      if (city) params.set('city', city);
      if (priorityOnly) params.set('priority', '1');
      const data = await api.get(`/api/avaloirs?${params.toString()}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Load schema once
  useEffect(() => {
    const loadSchema = async () => {
      try {
        const schemaData = await api.get('/api/survey/schema');
        setSchema(schemaData);
      } catch (e) {
        console.error('Erreur chargement schéma:', e);
      }
    };
    loadSchema();
  }, []);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, city, priorityOnly, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const exportXlsxUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (city) params.set('city', city);
    if (priorityOnly) params.set('priority', '1');
    const qs = params.toString();
    return qs ? `/api/exports/xlsx?${qs}` : '/api/exports/xlsx';
  }, [search, city, priorityOnly]);

  // Export Excel : fetch + blob download avec feedback visuel
  const [xlsxExporting, setXlsxExporting] = useState(false);
  const onExportXlsx = async () => {
    if (xlsxExporting) return;
    setXlsxExporting(true);
    try {
      await api.download(exportXlsxUrl, 'vertuoso-avaloirs.xlsx');
    } catch (e) {
      setError(`Export Excel : ${e.message || 'erreur'}`);
    } finally {
      setXlsxExporting(false);
    }
  };

  // Get ~6 first questions by position
  const displayQuestions = useMemo(() => {
    if (!schema?.questions) return [];
    return schema.questions
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .slice(0, 6);
  }, [schema]);

  // Extract unique cities from loaded items for city filter
  const citiesSet = useMemo(() => {
    const cities = new Set();
    items.forEach((item) => {
      if (item.city) cities.add(item.city);
    });
    return Array.from(cities).sort();
  }, [items]);

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avaloir ? Cette action est irréversible.')) return;
    try {
      await api.del(`/api/avaloirs/${id}`);
      // Refresh: if last item on page, step back a page
      if (items.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        fetchItems();
      }
    } catch (e) {
      alert('Suppression impossible : ' + (e.message || 'erreur'));
    }
  };

  return (
    <div className="vrt-list">
      <Header />

      <main className="vrt-list__main">
        <div className="vrt-list__head">
          <h1 className="vrt-list__title">
            {mode === 'avaloirs' ? 'Avaloirs référencés' : 'Poubelles & Cendriers'}
          </h1>
          <span className="vrt-list__badge" aria-label={mode === 'avaloirs' ? `${total} avaloirs` : `${quickItems.length} quick-points`}>
            {mode === 'avaloirs'
              ? `${total} ${total > 1 ? 'avaloirs' : 'avaloir'}`
              : `${quickItems.length} ${quickItems.length > 1 ? 'points' : 'point'}`}
          </span>
        </div>

        {/* Onglets : Avaloirs / Poubelles & Cendriers */}
        <div className="vrt-list__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'avaloirs'}
            className={`vrt-list__tab ${mode === 'avaloirs' ? 'vrt-list__tab--active' : ''}`}
            onClick={() => setMode('avaloirs')}
          >
            Avaloirs
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'quick'}
            className={`vrt-list__tab ${mode === 'quick' ? 'vrt-list__tab--active' : ''}`}
            onClick={() => setMode('quick')}
          >
            Poubelles & Cendriers
          </button>
        </div>

        {mode === 'quick' ? (
          <QuickPointsSection
            items={quickItems}
            loading={quickLoading}
            typeFilter={quickTypeFilter}
            setTypeFilter={setQuickTypeFilter}
            isAdmin={isAdmin}
            onDelete={onDeleteQuick}
            error={error}
            selectedIds={selectedQuickIds}
            onToggleSelect={toggleQuickSelect}
            allSelected={allQuickSelected}
            onToggleAll={toggleAllQuick}
          />
        ) : (
        <>
        <div className="vrt-list__filters">
          <label className="vrt-list__search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              className="vrt-input"
              placeholder="Rechercher…"
              value={rawSearch}
              onChange={(e) => setRawSearch(e.target.value)}
              aria-label="Rechercher"
            />
          </label>

          <select
            className="vrt-input vrt-list__select"
            value={city}
            onChange={(e) => { setCity(e.target.value); setPage(0); }}
            aria-label="Filtrer par ville"
          >
            <option value="">Toutes les villes</option>
            {citiesSet.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <label className="vrt-list__checkbox-label">
            <input
              type="checkbox"
              checked={priorityOnly}
              onChange={(e) => { setPriorityOnly(e.target.checked); setPage(0); }}
              aria-label="Afficher uniquement les prioritaires Life"
            />
            <span>Priorité Life</span>
          </label>

          <button
            type="button"
            onClick={onExportXlsx}
            disabled={xlsxExporting}
            className="vrt-btn vrt-btn--teal"
            aria-label={xlsxExporting ? 'Génération Excel en cours' : 'Exporter en Excel'}
            style={{ border: 'none', cursor: xlsxExporting ? 'progress' : 'pointer', opacity: xlsxExporting ? 0.85 : 1 }}
          >
            {xlsxExporting
              ? <Loader2 size={18} style={{ animation: 'vrt-spin-kf 1s linear infinite' }} />
              : <FileSpreadsheet size={18} />
            }
            <span>{xlsxExporting ? 'Génération…' : 'Exporter Excel'}</span>
          </button>
        </div>

        {error && (
          <div className="vrt-list__error" role="alert">
            Erreur : {error}
          </div>
        )}

        {loading ? (
          <div className="vrt-list__loading">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="vrt-list__empty">
            Aucun avaloir pour le moment.{' '}
            <Link to="/capture">Ajouter un avaloir</Link>
          </div>
        ) : (
          <div className="vrt-list__tablewrap">
            <table className="vrt-list__table">
              <thead>
                <tr>
                  {isAdmin && (
                    <th>
                      <input
                        type="checkbox"
                        checked={allAvaloirsSelected}
                        onChange={toggleAllAvaloirs}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                  )}
                  <th>Code</th>
                  <th>Photo</th>
                  <th>Ville</th>
                  <th>Coord.</th>
                  <th>Date</th>
                  <th>Priorité</th>
                  {displayQuestions.map((q) => (
                    <th key={q.key}>{q.label}</th>
                  ))}
                  <th>Carte</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <Row
                    key={a.id}
                    a={a}
                    isAdmin={isAdmin}
                    onDelete={onDelete}
                    displayQuestions={displayQuestions}
                    schema={schema}
                    selected={selectedAvaloirIds.has(a.id)}
                    onToggleSelect={() => toggleAvaloirSelect(a.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {items.length > 0 && totalPages > 1 && (
          <div className="vrt-list__pager">
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Précédent
            </button>
            <span className="vrt-list__pager-info">Page {page + 1} / {totalPages}</span>
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </button>
          </div>
        )}
        </>
        )}

        {/* Barre d'action sticky : sélection multiple (admin only) */}
        {isAdmin && mode === 'avaloirs' && selectedAvaloirIds.size > 0 && (
          <div className="vrt-list__actionbar" role="region" aria-label="Actions sur sélection">
            <span className="vrt-list__actionbar-count">
              {selectedAvaloirIds.size} sélectionné{selectedAvaloirIds.size > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              onClick={() => setSelectedAvaloirIds(new Set())}
              disabled={bulkDeleting}
            >
              Annuler
            </button>
            <button
              type="button"
              className="vrt-list__actionbar-del"
              onClick={bulkDeleteAvaloirs}
              disabled={bulkDeleting}
            >
              {bulkDeleting
                ? <><Loader2 size={16} style={{ animation: 'vrt-spin-kf 1s linear infinite' }} /> Suppression…</>
                : <><Trash2 size={16} /> Supprimer la sélection</>}
            </button>
          </div>
        )}
        {isAdmin && mode === 'quick' && selectedQuickIds.size > 0 && (
          <div className="vrt-list__actionbar" role="region" aria-label="Actions sur sélection">
            <span className="vrt-list__actionbar-count">
              {selectedQuickIds.size} sélectionné{selectedQuickIds.size > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              onClick={() => setSelectedQuickIds(new Set())}
              disabled={bulkDeleting}
            >
              Annuler
            </button>
            <button
              type="button"
              className="vrt-list__actionbar-del"
              onClick={bulkDeleteQuick}
              disabled={bulkDeleting}
            >
              {bulkDeleting
                ? <><Loader2 size={16} style={{ animation: 'vrt-spin-kf 1s linear infinite' }} /> Suppression…</>
                : <><Trash2 size={16} /> Supprimer la sélection</>}
            </button>
          </div>
        )}
      </main>

      <Footer />

      <style>{`
        @keyframes vrt-spin-kf { to { transform: rotate(360deg); } }
        .vrt-list { min-height: 100vh; background: var(--vrt-bg); }
        .vrt-list__main { max-width: 1200px; margin: 0 auto; padding: 20px 16px 40px; }
        .vrt-list__tabs {
          display: flex;
          gap: 6px;
          margin: 0 0 16px;
          border-bottom: 2px solid var(--vrt-border);
        }
        .vrt-list__tab {
          background: transparent;
          border: none;
          padding: 10px 16px;
          font-size: 15px;
          font-weight: 600;
          color: var(--vrt-text-muted);
          cursor: pointer;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          transition: color .15s ease, border-color .15s ease;
          min-height: 44px;
        }
        .vrt-list__tab:hover { color: var(--vrt-navy); }
        .vrt-list__tab--active {
          color: var(--vrt-navy);
          border-bottom-color: var(--vrt-teal);
        }
        .vrt-list__muted { color: var(--vrt-text-muted); }
        .vrt-list__code {
          display: inline-block;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-weight: 700;
          color: var(--vrt-navy);
          letter-spacing: 0.05em;
          padding: 3px 8px;
          background: var(--vrt-bg-alt);
          border-radius: 6px;
          font-size: 13px;
          text-decoration: none;
        }
        .vrt-list__code:hover { background: var(--vrt-teal-100); }
        .vrt-list__row--selected { background: rgba(46, 182, 167, 0.08); }
        .vrt-list__row--selected:hover { background: rgba(46, 182, 167, 0.14); }
        .vrt-list__actionbar {
          position: sticky;
          bottom: 16px;
          margin: 16px auto 0;
          max-width: 600px;
          background: var(--vrt-navy);
          color: #fff;
          padding: 12px 16px;
          border-radius: 999px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.25);
          display: flex;
          align-items: center;
          gap: 12px;
          z-index: 100;
        }
        .vrt-list__actionbar-count {
          font-weight: 700;
          font-size: 14px;
          flex: 1;
        }
        .vrt-list__actionbar .vrt-btn--ghost {
          color: #fff;
          border-color: rgba(255,255,255,0.3);
          background: transparent;
        }
        .vrt-list__actionbar-del {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--vrt-danger);
          color: #fff;
          border: none;
          border-radius: 999px;
          padding: 8px 14px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          min-height: 40px;
        }
        .vrt-list__actionbar-del:hover:not(:disabled) { filter: brightness(1.1); }
        .vrt-list__actionbar-del:disabled { opacity: 0.7; cursor: progress; }
        @media (max-width: 480px) {
          .vrt-list__actionbar { flex-wrap: wrap; padding: 10px 14px; border-radius: 14px; }
          .vrt-list__actionbar-count { flex: 1 1 100%; }
        }
        .vrt-list__head {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
        }
        .vrt-list__title { margin: 0; font-size: 22px; color: var(--vrt-navy); }
        .vrt-list__badge {
          display: inline-block; padding: 6px 14px; border-radius: 999px;
          background: var(--vrt-teal-100); color: var(--vrt-teal-700);
          font-weight: 700; font-size: 14px;
        }
        .vrt-list__filters {
          display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px;
          align-items: center;
        }
        .vrt-list__search { position: relative; flex: 1 1 240px; }
        .vrt-list__search svg {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: var(--vrt-text-muted);
        }
        .vrt-list__search .vrt-input { padding-left: 36px; }
        .vrt-list__select { flex: 0 1 180px; min-height: var(--vrt-touch); }
        .vrt-list__checkbox-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0 12px;
          height: 40px;
          border: 1px solid var(--vrt-border);
          border-radius: 6px;
          background: #fff;
          font-size: 14px;
          font-weight: 500;
          color: var(--vrt-navy);
          cursor: pointer;
        }
        .vrt-list__checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: var(--vrt-teal);
        }
        .vrt-list__tablewrap { overflow-x: auto; border: 1px solid var(--vrt-border); border-radius: 12px; }
        .vrt-list__table {
          width: 100%; border-collapse: collapse; min-width: 900px; font-size: 14px;
        }
        .vrt-list__table th, .vrt-list__table td {
          padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--vrt-border);
          vertical-align: middle;
        }
        .vrt-list__table thead th {
          background: var(--vrt-bg-alt); color: var(--vrt-navy);
          font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .04em;
          position: sticky; top: 0;
        }
        .vrt-list__thumb {
          width: 60px; height: 60px; object-fit: cover; border-radius: 8px;
          background: #e9ecef; display: block;
        }
        .vrt-list__thumb--placeholder {
          width: 60px; height: 60px; border-radius: 8px; background: #e9ecef;
          display: inline-flex; align-items: center; justify-content: center;
          color: #8a93a3; font-size: 11px;
        }
        .vrt-list__city-cell {
          font-weight: 600;
          color: var(--vrt-navy);
        }
        .vrt-list__city-code {
          display: block;
          font-size: 11px;
          color: var(--vrt-text-muted);
          font-weight: 400;
        }
        .vrt-list__priority-badge {
          display: inline-block;
          background: #c0392b;
          color: #fff;
          padding: 4px 10px;
          border-radius: 999px;
          font-weight: 700;
          font-size: 12px;
        }
        .vrt-list__anomaly-badge {
          display: inline-block;
          background: #7c3aed;
          color: #fff;
          padding: 4px 10px;
          border-radius: 999px;
          font-weight: 700;
          font-size: 12px;
        }
        .vrt-list__pager {
          display: flex; align-items: center; gap: 12px; justify-content: center;
          margin-top: 16px; flex-wrap: wrap;
        }
        .vrt-list__pager-info { color: var(--vrt-text-muted); font-size: 14px; }
        .vrt-list__loading, .vrt-list__empty {
          padding: 30px; text-align: center; color: var(--vrt-text-muted);
          background: var(--vrt-bg-alt); border-radius: 12px;
        }
        .vrt-list__error {
          background: #fdecea; color: #a23123; padding: 10px 14px;
          border-radius: 8px; margin-bottom: 12px;
        }
        .vrt-list__del {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 6px 10px; border-radius: 8px; border: 1px solid var(--vrt-danger);
          background: #fff; color: var(--vrt-danger); font-weight: 600;
          font-size: 13px; min-height: 36px;
        }
        .vrt-list__del:hover { background: var(--vrt-danger); color: #fff; }
        .vrt-list__map-link {
          display: inline-flex; align-items: center; gap: 4px;
          color: var(--vrt-teal-700); font-weight: 600; text-decoration: none;
          font-size: 13px;
        }
        .vrt-list__map-link:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}

function Row({ a, isAdmin, onDelete, displayQuestions, schema, selected, onToggleSelect }) {
  const [imgErr, setImgErr] = useState(false);
  const gmaps = `https://www.google.com/maps?q=${a.latitude},${a.longitude}`;
  const dateStr = a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : '';

  const questionMap = schema?.questions ? Object.fromEntries(schema.questions.map((q) => [q.key, q])) : {};

  return (
    <tr className={selected ? 'vrt-list__row--selected' : ''}>
      {isAdmin && (
        <td>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label="Sélectionner cet avaloir"
          />
        </td>
      )}
      {/* Code */}
      <td>
        <Link to={`/avaloir/${a.id}`} className="vrt-list__code">{a.code || '—'}</Link>
      </td>
      {/* Photo */}
      <td>
        {a.photo_url && !imgErr ? (
          <a href={a.photo_url} target="_blank" rel="noreferrer">
            <img
              src={a.photo_url}
              alt="Avaloir"
              className="vrt-list__thumb"
              onError={() => setImgErr(true)}
            />
          </a>
        ) : (
          <span className="vrt-list__thumb--placeholder">—</span>
        )}
      </td>

      {/* City */}
      <td>
        <div className="vrt-list__city-cell">
          {a.city || '—'}
          {a.city_code && <span className="vrt-list__city-code">{a.city_code}</span>}
        </div>
      </td>

      {/* Coordinates */}
      <td>
        {Number.isFinite(a.latitude) && Number.isFinite(a.longitude)
          ? `${a.latitude.toFixed(4)}, ${a.longitude.toFixed(4)}`
          : '—'}
      </td>

      {/* Date */}
      <td style={{ fontSize: '12px', color: 'var(--vrt-text-muted)' }}>
        {dateStr || '—'}
      </td>

      {/* Priorité Life + Anomalie */}
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {a.priority_life && (
            <span className="vrt-list__priority-badge">LIFE</span>
          )}
          {a.anomaly && (
            <span className="vrt-list__anomaly-badge">ANOMALIE</span>
          )}
          {!a.priority_life && !a.anomaly && '—'}
        </div>
      </td>

      {/* Dynamic question columns */}
      {displayQuestions.map((question) => {
        const value = a.answers?.[question.key];
        const resolved = value !== undefined ? resolveAnswer(question, value) : null;
        return (
          <td key={question.key} style={{ fontSize: '12px', color: 'var(--vrt-text-muted)' }}>
            {resolved || '—'}
          </td>
        );
      })}

      {/* Map link */}
      <td>
        {Number.isFinite(a.latitude) && Number.isFinite(a.longitude) && (
          <a href={gmaps} target="_blank" rel="noreferrer" className="vrt-list__map-link">
            <ExternalLink size={14} /> Voir
          </a>
        )}
      </td>

      {/* Delete button (admin only) */}
      {isAdmin && (
        <td>
          <button
            type="button"
            className="vrt-list__del"
            onClick={() => onDelete(a.id)}
            aria-label={`Supprimer l'avaloir ${a.id}`}
          >
            <Trash2 size={14} /> Supprimer
          </button>
        </td>
      )}
    </tr>
  );
}

function QuickPointsSection({ items, loading, typeFilter, setTypeFilter, isAdmin, onDelete, error, selectedIds, onToggleSelect, allSelected, onToggleAll }) {
  return (
    <>
      <div className="vrt-list__filters">
        <select
          className="vrt-input vrt-list__select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filtrer par type"
        >
          <option value="all">Tous les types</option>
          <option value="poubelle">Poubelles uniquement</option>
          <option value="cendrier">Cendriers uniquement</option>
          <option value="zone_sensible">Zones sensibles uniquement</option>
        </select>
      </div>

      {error && (
        <div className="vrt-list__error" role="alert">
          Erreur : {error}
        </div>
      )}

      {loading ? (
        <div className="vrt-list__loading">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="vrt-list__empty">
          Aucune poubelle ni cendrier pour le moment. Ils s'ajoutent depuis la page d'accueil.
        </div>
      ) : (
        <div className="vrt-list__tablewrap">
          <table className="vrt-list__table">
            <thead>
              <tr>
                {isAdmin && (
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onToggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                )}
                <th>Code</th>
                <th>Type</th>
                <th>Ville</th>
                <th>Coord.</th>
                <th>Précision</th>
                <th>Date</th>
                <th>Carte</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((qp) => (
                <QuickPointRow
                  key={qp.id}
                  qp={qp}
                  isAdmin={isAdmin}
                  onDelete={onDelete}
                  selected={selectedIds?.has(qp.id)}
                  onToggleSelect={() => onToggleSelect?.(qp.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function QuickPointRow({ qp, isAdmin, onDelete, selected, onToggleSelect }) {
  const meta = {
    poubelle:      { color: '#eab308', label: 'Poubelle' },
    cendrier:      { color: '#f97316', label: 'Cendrier' },
    zone_sensible: { color: '#ef4444', label: 'Zone sensible' },
  }[qp.type] || { color: '#6b7c93', label: qp.type };
  const { color, label } = meta;
  const gmaps = `https://www.google.com/maps?q=${qp.latitude},${qp.longitude}`;
  const when = qp.created_at ? new Date(qp.created_at).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '—';

  return (
    <tr className={selected ? 'vrt-list__row--selected' : ''}>
      {isAdmin && (
        <td>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            aria-label="Sélectionner ce point"
          />
        </td>
      )}
      <td>
        <span className="vrt-list__code">{qp.code || '—'}</span>
      </td>
      <td>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: color, color: '#fff',
          padding: '3px 10px', borderRadius: 999,
          fontSize: 12, fontWeight: 700,
        }}>
          {label}
        </span>
      </td>
      <td>{qp.city || <span className="vrt-list__muted">—</span>}</td>
      <td className="vrt-list__coords">
        {Number.isFinite(qp.latitude) && Number.isFinite(qp.longitude)
          ? `${qp.latitude.toFixed(5)}, ${qp.longitude.toFixed(5)}`
          : '—'}
      </td>
      <td>
        {Number.isFinite(qp.accuracy_m) ? `±${Math.round(qp.accuracy_m)} m` : '—'}
      </td>
      <td>{when}</td>
      <td>
        {Number.isFinite(qp.latitude) && Number.isFinite(qp.longitude) && (
          <a href={gmaps} target="_blank" rel="noreferrer" className="vrt-list__map-link">
            <ExternalLink size={14} /> Voir
          </a>
        )}
      </td>
      {isAdmin && (
        <td>
          <button
            type="button"
            className="vrt-list__del"
            onClick={() => onDelete(qp)}
            aria-label={`Supprimer ${label.toLowerCase()}`}
          >
            <Trash2 size={14} /> Supprimer
          </button>
        </td>
      )}
    </tr>
  );
}
